import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import axiosInstance from '../axiosInstance';
import { toast } from '../components/toast';

/**
 * VideoCallContext — manages the lifecycle of 1-to-1 video calls.
 *
 * Call states drive UI at <VideoCallRoot/>:
 *   - null                                       → nothing
 *   - requested + my_role=caller                 → "Ringing..." modal (with cancel)
 *   - requested + my_role=recipient              → IncomingCallModal (accept/decline)
 *   - accepted|active                            → full-screen VideoCallRoom
 */
const VideoCallContext = createContext({
  currentCall: null,
  loading: false,
  isFullscreenOpen: false,
  openFullscreen: () => {},
  closeFullscreen: () => {},
  requestCall: async () => {},
  acceptCall: async () => {},
  declineCall: async () => {},
  endCall: async () => {},
  fetchCallInfo: async () => ({ enabled: false }),
  refreshActiveCall: async () => {},
});

export const useVideoCall = () => useContext(VideoCallContext);

// Poll for active calls every 3s — fast enough that the recipient's ring UI
// appears within seconds of the caller placing the call, even when the toast
// polling notification channel is throttled / blocked / backgrounded.
const POLL_INTERVAL_MS = 3000;

export const VideoCallProvider = ({ children }) => {
  const [currentCall, setCurrentCall] = useState(null);
  const [loading, setLoading] = useState(false);
  // Fullscreen call UI is only opened when the user explicitly asks for it
  // (clicks the chat bubble or hits Accept from the toast). Defaults to false
  // so the ringing UI is a small toast, not a disruptive full-screen modal.
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const pollTimer = useRef(null);
  const token = useSelector((s) => s.auth?.token);

  const refreshActiveCall = useCallback(async () => {
    if (!token) {
      setCurrentCall(null);
      return;
    }
    try {
      const res = await axiosInstance.get('/calls/active');
      if (res.data.success) {
        setCurrentCall(res.data.data); // may be null
      }
    } catch {
      // Silent — we poll, no need to shout
    }
  }, [token]);

  // Poll active call while logged in
  useEffect(() => {
    if (!token) {
      setCurrentCall(null);
      if (pollTimer.current) clearInterval(pollTimer.current);
      return;
    }
    refreshActiveCall();
    pollTimer.current = setInterval(refreshActiveCall, POLL_INTERVAL_MS);

    // Toast manager fires this when an incoming_call notification is seen
    const handleRefresh = () => refreshActiveCall();
    window.addEventListener('videocall:refresh', handleRefresh);

    // When tab regains focus, refresh immediately — browsers throttle
    // setInterval on backgrounded tabs to as slow as once per minute,
    // so without this the IncomingCallModal would lag behind.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshActiveCall();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleRefresh);

    // Request browser notification permission once so we can alert the
    // user on incoming calls even when the tab is backgrounded.
    if (typeof Notification !== 'undefined'
        && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch {}
    }

    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
      window.removeEventListener('videocall:refresh', handleRefresh);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleRefresh);
    };
  }, [token, refreshActiveCall]);

  const fetchCallInfo = useCallback(async (userId) => {
    try {
      const res = await axiosInstance.get(`/users/${userId}/call-info`);
      return res.data.success ? res.data.data : { enabled: false };
    } catch {
      return { enabled: false };
    }
  }, []);

  const requestCall = useCallback(async (recipientId) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await axiosInstance.post('/calls/request', { recipient_id: recipientId });
      if (res.data.success) {
        setCurrentCall(res.data.data);
        return res.data.data;
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start call');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const acceptCall = useCallback(async (callId) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await axiosInstance.post(`/calls/${callId}/accept`);
      if (res.data.success) {
        setCurrentCall(res.data.data);
        return res.data.data;
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to accept call');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const declineCall = useCallback(async (callId) => {
    try {
      await axiosInstance.post(`/calls/${callId}/decline`);
    } catch {} finally {
      setCurrentCall(null);
    }
  }, []);

  const endCall = useCallback(async () => {
    if (!currentCall) return;
    try {
      await axiosInstance.post(`/calls/${currentCall.id}/end`);
    } catch {} finally {
      setCurrentCall(null);
      setIsFullscreenOpen(false);
    }
  }, [currentCall]);

  const openFullscreen = useCallback(() => setIsFullscreenOpen(true), []);
  const closeFullscreen = useCallback(() => setIsFullscreenOpen(false), []);

  // Auto-open fullscreen:
  //   1. Caller side immediately when the call is placed (they want to see
  //      who they're calling and have a big Cancel button)
  //   2. Both sides when the call becomes accepted/active (video room)
  // Recipient stays on the toast during the 'requested' phase so they can
  // accept/decline from anywhere without a disruptive full-screen takeover.
  useEffect(() => {
    if (!currentCall) { setIsFullscreenOpen(false); return; }

    if (currentCall.status === 'requested' && currentCall.my_role === 'caller') {
      setIsFullscreenOpen(true);
    } else if (currentCall.status === 'accepted' || currentCall.status === 'active') {
      setIsFullscreenOpen(true);
    } else if (['ended', 'declined', 'missed', 'failed'].includes(currentCall.status)) {
      setIsFullscreenOpen(false);
    }
  }, [currentCall?.status, currentCall?.my_role]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <VideoCallContext.Provider
      value={{
        currentCall,
        loading,
        isFullscreenOpen,
        openFullscreen,
        closeFullscreen,
        requestCall,
        acceptCall,
        declineCall,
        endCall,
        fetchCallInfo,
        refreshActiveCall,
        setCurrentCall,
      }}
    >
      {children}
    </VideoCallContext.Provider>
  );
};

export default VideoCallContext;
